/* eslint relay/unused-fields: 0 */
import { useEffect } from 'react';
import { graphql, useFragment } from 'react-relay';
import serverVars from 'server-vars';
import seedrandom from 'seedrandom';
import { trackEvent, eventNameConstants } from 'dibs-tracking';
import { getAbUuid } from 'dibs-cookie-jar/exports/abTestHelpers';
import type { AbTestV2 } from './types';
import {
    clientAbTestV2_useAbTestV2BuyerUser_user$data,
    clientAbTestV2_useAbTestV2BuyerUser_user$key,
} from './__generated__/clientAbTestV2_useAbTestV2BuyerUser_user.graphql';

type AbTestEventOptions = Parameters<typeof trackEvent>[2];

export const AUDIENCE = {
    STANDARD: 'STANDARD',
    RESPONSIVE: 'RESPONSIVE',
    MOBILE: 'MOBILE',
} as const;

/**
 * This is the core bucketing function for the client side A/B testing.
 * Do not change this logic without considering the implications for the
 * the CDN-based bucketing (in dibs-fastly-js repo).
 */
const clientABTestV2Map = new Map<string, string>();
// Exported for testing, do not use directly. Use getAbTestV2 instead.
export function __getClientABTestV2(config: AbTestV2['config']): AbTestV2 | null {
    const userGuid = getAbUuid(document.cookie);
    if (
        userGuid &&
        config.isActive &&
        config.testName &&
        config.variations?.length &&
        config.ramp
    ) {
        const seed = userGuid + config.testName;
        // Is this combination of user and test already bucketed?
        const cachedVariant = clientABTestV2Map.get(seed);
        if (cachedVariant) {
            return { variant: cachedVariant, config };
        }
        // seed the random number generator
        const bucketRng = seedrandom(seed);
        // total weight must sum to 100; get random number between 0 and 100
        const ranNum = bucketRng() * 100;
        let variantRn = ranNum;
        // find the variant that the random number falls into
        const variant = (config?.variations || []).find(v => {
            if (typeof v?.weight !== 'number') {
                return false;
            }
            variantRn = variantRn - v.weight;
            return variantRn < 0;
        });
        if (variant && variant.value) {
            // If the visitor falls in a variant, are they in the ramp?
            // Create a new random number generator with the
            // vistor's variant seeded random number
            const rampRng = seedrandom(ranNum.toString());
            // Does the new random number fall in the ramp?
            // This ensures that the same visitor will always see the same
            // variation as the ramp goes up
            if (rampRng() * 100 < config.ramp) {
                clientABTestV2Map.set(seed, variant.value);
                return { variant: variant.value, config };
            }
        }
    }
    return null;
}

export function getAbTestV2(testName: string): AbTestV2 | null {
    if (typeof window === 'undefined') {
        throw new Error('getAbTestV2 should only be called on the client!');
    }
    // get the { variant?, config } object for the test
    const abTest = serverVars.get('abTestsV2.' + testName) as AbTestV2 | null | undefined;
    if (!abTest) {
        // maybe we don't have this test
        return null;
    }

    if (abTest.variant) {
        // the test bucket has been determined in the CDN
        // this happens for both BUYER_COOKIE and opted-in tests
        return abTest;
    } else if (abTest?.config?.testType === 'BUYER_CLIENT') {
        // we need to make the test decision on the client
        return __getClientABTestV2(abTest.config as AbTestV2['config']);
    }
    // not a BUYER_CLIENT test or request is eligible for for BUYER_COOKIE test
    return null;
}

function trackAbTestEvent({
    variant,
    testName,
    ramp,
    options,
}: {
    variant: string | undefined;
    testName: string | undefined;
    ramp: number | undefined;
    options: AbTestEventOptions;
}): void {
    trackEvent(
        {
            category: 'a/b testing v3',
            eventName: eventNameConstants.EVENT_AB_TESTING,
            action: testName,
            interaction_type: testName,
            label: variant,
            step_interaction_name: variant,
            value: ramp,
            ab_uuidCookie: getAbUuid(document.cookie),
        },
        null,
        options
    );
}

export function trackAbTestV2Variant(testName: string, options?: AbTestEventOptions): void {
    const abTest = getAbTestV2(testName);
    if (abTest) {
        trackAbTestEvent({
            variant: abTest.variant,
            testName,
            ramp: abTest.config.ramp ?? undefined,
            options,
        });
    }
}

type AbTestV2Type =
    | NonNullable<clientAbTestV2_useAbTestV2BuyerUser_user$data['abTestV2List']>[number]
    | null;

const trackedTests = new Set<string>();

export function useAbTestV2BuyerUser({
    testName,
    user: userRef,
    eventOptions,
    shouldFireTracking = true,
}: {
    testName: string;
    user: clientAbTestV2_useAbTestV2BuyerUser_user$key | null;
    eventOptions?: AbTestEventOptions;
    shouldFireTracking?: boolean;
}): AbTestV2Type {
    const user = useFragment(
        graphql`
            fragment clientAbTestV2_useAbTestV2BuyerUser_user on User {
                abTestV2List(testType: BUYER_USER) {
                    config {
                        createdDate
                        modifiedDate
                        testName
                        description
                        testType
                        audience
                        ramp
                        isActive
                        route
                        variations {
                            value
                            weight
                        }
                    }
                    variant
                }
            }
        `,
        userRef
    );

    const abTestV2 = user?.abTestV2List?.find(test => test?.config?.testName === testName) || null;

    const variant = abTestV2?.variant ?? undefined;
    const ramp = abTestV2?.config?.ramp ?? undefined;
    const immediate = eventOptions?.immediate;

    useEffect(() => {
        // check trackedTests to ensure that tracking is called only once
        // even if hook is mounted multiple times
        if (variant && !trackedTests.has(testName) && shouldFireTracking) {
            trackAbTestEvent({
                variant,
                testName,
                ramp,
                options: immediate !== undefined ? { immediate } : undefined,
            });
            trackedTests.add(testName);
        }
    }, [variant, testName, ramp, immediate, shouldFireTracking]);

    return abTestV2;
}
